import React, { MouseEventHandler, useEffect, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import InputText from 'components/common/InputText';
import dynamic from 'next/dynamic';
import { OptionTypeOfSurgicalHistory } from 'components/medicalHistory/types/types';
import MuiAutoComplete from 'components/common/autoComplete';
import useMuiAutoCompleteCreatable from 'hooks/useMuiAutoCompleteCreatable';
import dayjs, { Dayjs } from 'dayjs';
import Label from 'components/common/label';
import ErrorLabel from 'components/common/errorLabel';
import styles from 'components/common/common.module.scss';
import clsx from 'clsx';
import AddItemButton from 'components/common/addItemButton';
import { removeItemFromList } from 'components/medicalHistory/helpers';
import isNil from 'lodash/isNil';

const MuiDatePicker = dynamic(() => import('./datePicker'), { ssr: false });

const inputMaxDate = (yearOnly?: string) => dayjs().format(`${yearOnly ? 'YYYY' : 'YYYY-MM-DD'}`);
const inputMinDate = (yearOnly?: string) => dayjs('1900').format(yearOnly ? 'YYYY' : 'YYYY-MM-DD');

type ItemProps = {
  value: string;
  onClick: MouseEventHandler;
}

function Item({ value, onClick }: ItemProps) {
  return (
    <div className="flex justify-between items-center text-sm p-2.5 rounded autocomplete-Selected">
      <p>{value}</p>
      <ClearIcon
        onClick={onClick}
        fontSize="small"
      />
    </div>
  );
}

enum CustomInputFields {
  Cancer = 'Cancer',
  Other = 'Other',
}

type Props = {
  option: OptionTypeOfSurgicalHistory[];
  value?: OptionTypeOfSurgicalHistory[];
  id: string;
  placeholder: string;
  onChange?: (items: OptionTypeOfSurgicalHistory[]) => void;
  isInputField?: boolean;
  isSurgical?: boolean;
};

function SearchableTextBox({
  option,
  placeholder,
  onChange,
  value,
  isInputField,
  isSurgical,
}: Props) {
  const [inputValue, setInputValue] = React.useState<
    OptionTypeOfSurgicalHistory[]
  >(value || []);
  const [inputValueYear, setInputValueYear] = React.useState<
    OptionTypeOfSurgicalHistory[]
  >(value || []);
  const [cancerValue, setCancerValue] = useState('');
  const [otherValue, setOtherValue] = useState('');
  const [hideSearchBox, setHideSearchBox] = useState(false);
  const filterOptions = useMuiAutoCompleteCreatable();

  const removeItem = (item: OptionTypeOfSurgicalHistory) => {
    if (isSurgical) {
      setInputValueYear((prev) => {
        const { items, isEmpty } = removeItemFromList(prev, item);
        setHideSearchBox(!isEmpty);
        return items;
      });
    } else {
      setInputValue((prev) => {
        const { items, isEmpty } = removeItemFromList(prev, item);
        setHideSearchBox(!isEmpty);
        return items;
      });
    }
  };

  useEffect(() => {
    if (value?.length) {
      setHideSearchBox(true);
    }
  }, [value]);

  useEffect(() => {
    if (onChange) {
      onChange(inputValue);
    }
  }, [inputValue]);

  useEffect(() => {
    if (onChange) {
      if (isSurgical) {
        onChange(inputValueYear);
      }
    }
  }, [inputValueYear]);

  const onChangeAutoComplete = (value: OptionTypeOfSurgicalHistory[]) => {
    if (isSurgical) {
      if (inputValueYear.length < value.length) {
        setInputValueYear(value);
      }
    } else if (inputValue.length < value.length) {
      setInputValue(value);
    }
    setHideSearchBox(true);
  };

  useEffect(() => {
    if (value) {
      if (isSurgical) {
        setInputValueYear(value);
      } else {
        setInputValue(value);
      }
    }
  }, [value]);

  const onChangeSurgeryYear = (
    name: string,
    value: Dayjs | null,
  ) => {
    const getYear = dayjs(value).format('YYYY');
    setInputValueYear(
      (old: OptionTypeOfSurgicalHistory[]) => old.filter((e: OptionTypeOfSurgicalHistory) => {
        if (e.value === name) {
          e.isError = !isNil(value) && !value?.isValid();
          e.year = !isNil(value) ? getYear : null;
          return e;
        } return e;
      }),
    );
  };

  const isEmptyOptions = (isSurgical ? inputValueYear.length === 0 : inputValue.length === 0);
  const addButtonClass = clsx((isEmptyOptions || hideSearchBox)
    ? 'addButton'
    : 'addButtonDisable');

  return (
    <div>
      {!isSurgical
        && inputValue.map((item) => (
          <div key={item.label} className="my-2">
            <Item
              value={item.value}
              onClick={() => { removeItem(item); }}
            />

            {isInputField && (
            <>
              {CustomInputFields.Cancer === item.label && (
              <div>
                <p className={styles.separator} />
                <Label>
                  What kind of cancer?
                </Label>
                <p className={styles.separator} />
                <InputText
                  onChange={(event) => { setCancerValue(event.target.value); }}
                  value={cancerValue}
                  className="w-full"
                  placeholder="Type..."
                  autoComplete="off"
                />
                <p className={styles.separator} />
              </div>
              )}
              {CustomInputFields.Other === item.label && (
                <InputText
                  onChange={(event) => { setOtherValue(event.target.value); }}
                  value={otherValue}
                  className="w-full my-2"
                  placeholder="Type..."
                  autoComplete="off"
                />
              )}
            </>
            )}
          </div>
        ))}

      {/* For Surgical History */}

      {isSurgical
        && inputValueYear.map((item, index) => {
          const surgeryObj = inputValueYear.find(
            (e: OptionTypeOfSurgicalHistory) => e.value === item.value,
          );
          const minDate = inputMinDate('YYYY');
          const maxDate = inputMaxDate('YYYY');

          const errorMsg = Number(surgeryObj?.year) < Number(minDate)
            || Number(surgeryObj?.year) > Number(maxDate)
            ? `Please select a year from ${inputMinDate('YYYY')} - ${inputMaxDate('YYYY')}` : '';

          return (
            <div key={item.label} className="my-2.5 overflow-hidden">
              <Item
                value={item.value}
                onClick={() => { removeItem(item); }}
              />

              {CustomInputFields.Other === item.label && (
                <InputText
                  onChange={(event) => { setOtherValue(event.target.value); }}
                  value={otherValue}
                  className="w-full my-2"
                  placeholder="Type..."
                  autoComplete="off"
                />
              )}

              <div className="flex flex-col mb-2.5">
                <Label>Year</Label>
                {surgeryObj?.isError && <ErrorLabel>{errorMsg}</ErrorLabel>}
                <MuiDatePicker
                  views={['year']}
                  value={!isNil(surgeryObj?.year) ? dayjs(surgeryObj?.year) : null}
                  onChange={(newValue) => {
                    onChangeSurgeryYear(item.value, newValue);
                  }}
                  disableFuture
                  inputFormat="YYYY"
                  error={surgeryObj?.isError}
                />
              </div>
            </div>
          );
        })}
      <div className="my-2.5">
        {option?.length > 0 && isSurgical ? (
          <MuiAutoComplete
            options={option}
            value={inputValueYear}
            onChange={(event, newInputValue) => {
              onChangeAutoComplete(newInputValue);
            }}
            placeholder={placeholder}
            filterOptions={filterOptions}
            hide={hideSearchBox}
          />
        ) : (
          <MuiAutoComplete
            options={option}
            value={inputValue}
            onChange={(event, newInputValue) => {
              onChangeAutoComplete(newInputValue);
            }}
            placeholder={placeholder}
            filterOptions={filterOptions}
            hide={hideSearchBox}
          />
        )}
      </div>
      <AddItemButton
        className={addButtonClass}
        label={isSurgical ? 'Add Surgery' : 'Add Medical History'}
        onClick={() => { setHideSearchBox(false); }}
        disabled={inputValue.length === 0 && !hideSearchBox}
      />
    </div>
  );
}

export default SearchableTextBox;
